import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts"
import { formatCurrency, formatPercent, colorPara } from "../helpers"

const TooltipPie = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null
  const d = payload[0].payload
  return (
    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
      <div style={{ fontWeight: 700 }}>{d.descripcion}</div>
      <div>{formatCurrency(d.total)}</div>
      <div style={{ color: "#94a3b8", fontSize: 11 }}>{formatPercent(d.pct_participacion)}</div>
    </div>
  )
}

TooltipPie.propTypes = { active: PropTypes.bool, payload: PropTypes.array }

const ListaSucursales = ({ items, onSelect, selected }) => (
  <div style={{ borderRight: "1px solid #e2e8f0", overflow: "auto", maxHeight: 480 }}>
    <button
      type="button"
      onClick={() => onSelect("__global__")}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "12px 16px",
        border: "none",
        background: selected === "__global__" ? "#eff6ff" : "transparent",
        color: selected === "__global__" ? "#1e40af" : "#0f172a",
        fontWeight: 600,
        fontSize: 13,
        borderBottom: "1px solid #f1f5f9",
        cursor: "pointer",
      }}
    >
      <i className="mdi mdi-earth me-2" />
      Global
    </button>
    {items.map(s => (
      <button
        key={s.sucursal}
        type="button"
        onClick={() => onSelect(s.sucursal)}
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          padding: "10px 16px",
          border: "none",
          background: selected === s.sucursal ? "#eff6ff" : "transparent",
          color: selected === s.sucursal ? "#1e40af" : "#475569",
          fontWeight: selected === s.sucursal ? 600 : 500,
          fontSize: 13,
          borderBottom: "1px solid #f1f5f9",
          cursor: "pointer",
        }}
      >
        <i className="mdi mdi-store me-2" style={{ color: "#94a3b8" }} />
        {s.sucursal}
      </button>
    ))}
  </div>
)

ListaSucursales.propTypes = { items: PropTypes.array, onSelect: PropTypes.func, selected: PropTypes.string }

const Distribucion = ({ distribucion }) => {
  if (!distribucion || distribucion.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-credit-card-outline" style={{ fontSize: 40, color: "#cbd5e1" }} />
        <div className="mt-2">Sin métodos de pago para mostrar.</div>
      </div>
    )
  }

  const opsAnormales = distribucion.filter(d => d.ops_anormales)

  return (
    <div style={{ padding: 20 }}>
      {opsAnormales.length > 0 && (
        <div
          style={{
            background: "#fef3c7",
            border: "1px solid #fde68a",
            color: "#92400e",
            padding: "10px 14px",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          <i className="mdi mdi-alert me-2" />
          <strong>{opsAnormales.length}</strong> método{opsAnormales.length !== 1 ? "s" : ""} con operaciones anormales detectadas.
        </div>
      )}

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 380px", minWidth: 320, height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={distribucion}
                dataKey="total"
                nameKey="descripcion"
                cx="50%"
                cy="50%"
                outerRadius={110}
                innerRadius={50}
                paddingAngle={1}
              >
                {distribucion.map((_, i) => (
                  <Cell key={i} fill={colorPara(i)} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<TooltipPie />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={{ flex: "1 1 380px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Método</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Total</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>%</th>
              </tr>
            </thead>
            <tbody>
              {distribucion.map((d, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "10px 12px", color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: colorPara(i) }} />
                    {d.descripcion}
                    {d.ops_anormales && (
                      <span style={{ background: "#fef3c7", color: "#92400e", padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                        ⚠ ANORMAL
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#0f172a", fontWeight: 600 }}>{formatCurrency(d.total)}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>{formatPercent(d.pct_participacion)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

Distribucion.propTypes = { distribucion: PropTypes.array }

const MetodosPagoTab = ({ data }) => {
  const mp = data?.metodos_pago
  const [selected, setSelected] = useState("__global__")

  const distribucion = useMemo(() => {
    if (!mp) return []
    if (selected === "__global__") return mp.global?.distribucion || []
    const suc = (mp.sucursales || []).find(s => s.sucursal === selected)
    return suc?.distribucion || []
  }, [mp, selected])

  if (!mp) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-credit-card-off-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin información de métodos de pago.</div>
      </div>
    )
  }

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", minHeight: 480 }}>
        <ListaSucursales items={mp.sucursales || []} onSelect={setSelected} selected={selected} />
        <Distribucion distribucion={distribucion} />
      </div>
    </div>
  )
}

MetodosPagoTab.propTypes = { data: PropTypes.object }

export default MetodosPagoTab
